import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FundContext } from '../FundPage';
import { useRemote } from '../../../../Utils/Utils';
import { Save, Trash2 } from 'react-feather';
import PageGridItem from '../../../Shared/Page/PageGridItem';
import Table from '../../../../components/Table/Table';
import Button from '../../../../components/Buttons/Button';
import FlexWrapper from '../../../FlexWrapper';
import { toastDanger, toastSuccess } from '../../../../components/popups/Toast';

const FundHoldingRationales = () => {
    const { fund, editMode, setSelectedTab, holdings } = useContext(FundContext);
    const remote = useRemote();

    // Rationales keyed by assetCode for quick lookup
    const [rationalesByCode, setRationalesByCode] = useState({});
    // assetCode -> assetId map (needed for POST of brand-new rationales)
    const [assetIdByCode, setAssetIdByCode] = useState({});
    // Per-row dirty text while editing
    const [editedText, setEditedText] = useState({});
    const [loading, setLoading] = useState(true);

    const fundNumber = fund?.fundNumber;

    useEffect(() => {
        setSelectedTab('Holdings Rationale');
    }, []);

    useEffect(() => {
        if (!fundNumber) return;
        setLoading(true);
        Promise.all([
            remote.get(`/funds/funds/${fundNumber}/holding-rationales`),
            remote.get('/assets/list'),
        ]).then(([rationales, assets]) => {
            setRationalesByCode(indexByAssetCode(rationales));
            setAssetIdByCode(buildAssetIdMap(assets));
        }).finally(() => setLoading(false));
    }, [fundNumber]);

    // Latest-date holdings, same logic as FundHoldings
    const latestHoldings = useMemo(() => {
        if (!holdings) return [];
        const dates = Object.keys(holdings);
        if (dates.length === 0) return [];
        const firstDate = dates[0];
        const list = holdings[firstDate] || [];
        return [...list].sort((a, b) => (b.currentWeight || 0) - (a.currentWeight || 0));
    }, [holdings]);

    function onChangeText(assetCode, value) {
        setEditedText(prev => ({ ...prev, [assetCode]: value }));
    }

    function isDirty(holding) {
        const edited = editedText[holding.assetCode];
        if (edited === undefined) return false;
        const existing = rationalesByCode[holding.assetCode]?.rationaleText || '';
        return edited !== existing;
    }

    function resolveAssetId(holding) {
        const existing = rationalesByCode[holding.assetCode];
        if (existing) return existing.assetId;
        return assetIdByCode[holding.assetCode];
    }

    function saveRow(holding) {
        const assetId = resolveAssetId(holding);
        if (!assetId) {
            toastDanger(`No matching asset found for ${holding.assetName || holding.assetCode}`);
            return;
        }
        const payload = {
            assetId,
            assetClass: holding.assetClass,
            rationaleText: editedText[holding.assetCode] || '',
        };
        remote.post(`/funds/funds/${fundNumber}/holding-rationales`, payload).then(saved => {
            setRationalesByCode(prev => ({ ...prev, [holding.assetCode]: { ...saved, assetCode: holding.assetCode } }));
            setEditedText(prev => {
                const next = { ...prev };
                delete next[holding.assetCode];
                return next;
            });
            toastSuccess('Rationale saved');
        }).catch(() => toastDanger('Failed to save rationale'));
    }

    function deleteRow(holding) {
        const existing = rationalesByCode[holding.assetCode];
        if (!existing) return;
        remote.delete(`/funds/holding-rationales/${existing.id}`).then(() => {
            setRationalesByCode(prev => {
                const next = { ...prev };
                delete next[holding.assetCode];
                return next;
            });
            setEditedText(prev => {
                const next = { ...prev };
                delete next[holding.assetCode];
                return next;
            });
            toastSuccess('Rationale deleted');
        }).catch(() => toastDanger('Failed to delete rationale'));
    }

    const headers = [
        { text: <div className='sorting-header left'>Asset</div> },
        { text: <div className='sorting-header left'>Asset Class</div> },
        { text: <div className='sorting-header left'>Rationale</div> },
    ];
    const colWidths = editMode ? ['25%', '20%', '55%'] : ['30%', '20%', '50%'];

    const dataMap = latestHoldings.map((holding) => {
        const existing = rationalesByCode[holding.assetCode];
        const currentText = editedText[holding.assetCode] !== undefined
            ? editedText[holding.assetCode]
            : existing?.rationaleText || '';

        return (
            <tr key={holding.assetCode || holding.assetName}>
                <td>{holding.assetName || '-'}</td>
                <td>{holding.assetClass || '-'}</td>
                <td>
                    {editMode ? (
                        <FlexWrapper gap='s' align='flex-start'>
                            <textarea
                                className='rationale-textarea'
                                value={currentText}
                                onChange={(e) => onChangeText(holding.assetCode, e.target.value)}
                                rows={2}
                                style={{ flex: 1, minHeight: '3rem', padding: '0.5rem', resize: 'vertical' }}
                            />
                            <FlexWrapper gap='xs' direction='col'>
                                <Button
                                    small
                                    iconOnly
                                    icon={<Save size={14} />}
                                    variant='positive'
                                    disabled={!isDirty(holding)}
                                    onClick={() => saveRow(holding)}
                                />
                                <Button
                                    small
                                    iconOnly
                                    icon={<Trash2 size={14} />}
                                    variant='danger'
                                    disabled={!existing}
                                    onClick={() => deleteRow(holding)}
                                />
                            </FlexWrapper>
                        </FlexWrapper>
                    ) : (
                        <span>{currentText || '-'}</span>
                    )}
                </td>
            </tr>
        );
    });

    return (
        <PageGridItem container col='1 / span 12' gap='l' direction='col'>
            <Table
                headers={headers}
                colWidths={colWidths}
                dataMap={dataMap}
                dataLoading={loading}
                height='calc(100vh - 26.5rem)'
                wrapperStyle={{ overflowY: 'auto' }}
                noResults={{ message: 'No holdings', subMessage: 'There are no holdings for this portfolio yet.' }}
            />
        </PageGridItem>
    );
};

function indexByAssetCode(rationales) {
    const map = {};
    (rationales || []).forEach(r => {
        if (r.assetCode) map[r.assetCode] = r;
    });
    return map;
}

function buildAssetIdMap(assets) {
    const map = {};
    (assets || []).forEach(a => {
        if (a.code) map[a.code] = a.id;
    });
    return map;
}

export default FundHoldingRationales;
